import { Link } from 'react-router-dom';
import { GoogleMark } from '@/components/reviews/GoogleMark';
import { Star, Stethoscope, IndianRupee, ArrowRight } from 'lucide-react';
import { SeoSection } from '@/components/seo/SeoLayout';
import type { LocalProofData } from '@/seo/localProof';

/**
 * The proof layer under a clinic list: who treats you, what people said, and
 * what it costs - drawn from the clinics on the page, never from copy. This is
 * what separates a location page from a doorway page in Google's eyes.
 */

function Stars({ value }: { value: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`size-3.5 ${n <= Math.round(value) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`}
        />
      ))}
    </span>
  );
}

const rupees = (n: number) => `₹${n.toLocaleString('en-IN')}`;

export function LocalProof({ data, place }: { data: LocalProofData; place: string }) {
  const { therapists, reviews, priceFrom, priceTo, clinicCount } = data;
  const hasPrice = priceFrom != null && priceTo != null;
  if (!therapists.length && !reviews.length && !hasPrice) return null;

  return (
    <>
      {hasPrice && (
        <SeoSection
          title={`What physiotherapy costs in ${place}`}
          description="Real session prices from the clinics listed above, not an estimate."
        >
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-3 rounded-2xl border bg-card px-5 py-4 shadow-sm">
              <IndianRupee className="size-5 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Sessions from</p>
                <p className="text-xl font-bold tracking-tight text-foreground">{rupees(priceFrom)}</p>
              </div>
            </div>
            {priceTo !== priceFrom && (
              <div className="flex items-center gap-3 rounded-2xl border bg-card px-5 py-4 shadow-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Up to</p>
                  <p className="text-xl font-bold tracking-tight text-foreground">{rupees(priceTo)}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 rounded-2xl border bg-card px-5 py-4 shadow-sm">
              <div>
                <p className="text-xs text-muted-foreground">Across</p>
                <p className="text-xl font-bold tracking-tight text-foreground">
                  {clinicCount} {clinicCount === 1 ? 'clinic' : 'clinics'}
                </p>
              </div>
            </div>
          </div>
        </SeoSection>
      )}

      {therapists.length > 0 && (
        <SeoSection
          title={`Physiotherapists in ${place}`}
          description="The people you would actually see, at the clinics above."
        >
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {therapists.slice(0, 9).map((t, i) => (
              <li key={`${t.clinicHref}-${t.name}-${i}`}>
                <Link
                  to={t.clinicHref}
                  className="group flex h-full gap-3 rounded-xl border bg-card p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--sc-teal-mid)] hover:shadow-[0_8px_24px_rgba(15,110,86,0.10)]"
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Stethoscope className="size-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-foreground">{t.name}</span>
                    {(t.qualification || t.specialisation) && (
                      <span className="block text-xs text-muted-foreground">
                        {[t.qualification, t.specialisation].filter(Boolean).join(' · ')}
                      </span>
                    )}
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {t.experience ? `${t.experience} yrs experience · ` : ''}
                      <span className="text-primary">{t.clinicName}</span>
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </SeoSection>
      )}

      {reviews.length > 0 && (
        <SeoSection
          title={`What patients in ${place} say`}
          description="From patients of these clinics, written on Recovry after a booking or on Google."
        >
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {reviews.slice(0, 6).map((r, i) => (
              <li key={`${r.clinicHref}-${i}`} className="flex h-full flex-col rounded-xl border bg-card p-4 shadow-sm">
                <Stars value={r.rating} />
                <p className="mt-2 line-clamp-4 text-sm leading-relaxed text-foreground">“{r.comment}”</p>
                <p className="mt-auto pt-3 text-xs text-muted-foreground">
                  {r.user ? `${r.user} · ` : ''}
                  {r.source === 'google' ? (
                    <GoogleMark className="mr-1 inline size-2.5 align-[-1px]" />
                  ) : (
                    'Verified visit · '
                  )}
                  <Link to={r.clinicHref} className="inline-flex items-center gap-1 text-primary hover:underline">
                    {r.clinicName} <ArrowRight className="size-3" />
                  </Link>
                </p>
              </li>
            ))}
          </ul>
        </SeoSection>
      )}
    </>
  );
}
